import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import googleDriveLogo from '../../../utils/integrations/google-drive.svg';

import slackLogo from '../../../utils/integrations/slack.svg';
import pipedriveLogo from '../../../utils/integrations/pipedrive.svg';
import hubspotLogo from '../../../utils/integrations/hubspot.svg';
import { LANGUAGE_NAMES } from '../../../constants';

export function getTemplates(t, i18n) {
  let BASE_SUMMARY_PROMPT = t('Sammanfatta följande mötestranskription enligt denna struktur:\n\n## Sammanfattning\n\nKort sammanfattning av mötet (1–3 meningar)\n\n## Viktiga ämnen\n\n- **[Ämne 1]**: Kort beskrivning av vad som diskuterades inom detta ämne.\n- **[Ämne 2]**: Kort beskrivning av ett annat viktigt ämne från mötet.\n\n## Uppföljningspunkter för Talare X\n\n- Lista tydliga uppgifter eller åtgärder som tilldelades Talare X.\n\nAnalysera transkriptionen noggrant och håll dig till en tydlig, koncis och informativ stil. Förenkla utan att tappa viktiga detaljer.\n\n- Sammanfattningen ska alltid finnas med.\n- Om det inte finns några viktiga ämnen, skriv "Inga viktiga ämnen diskuterades".\n- Om det inte finns några uppföljningspunkter, skriv "Inga uppföljningspunkter tilldelades".\n- Skriv en lista av uppföljningspunkter för varje talare i transkriptet, och använd deras namn som rubrik för varje talare.\n- Om det inte finns några uppföljningspunkter tilldelade, skriv "Inga uppföljningspunkter för Talare X');

  const language = LANGUAGE_NAMES[i18n.language] || i18n.language;

  BASE_SUMMARY_PROMPT += `\n- ${t('Skriva bara på {{language}}.', { language })}`;

  return [
    {
      id: 'interview',
      title: t('Intervjumall'),
      description: t('Sammanfatta en intervju och ta ut frågor och svar'),
      icons: [EditOutlinedIcon, DescriptionOutlinedIcon],
      popular: true,
      data: {
        edges: [
          {
            id: 'xy-edge__1741681888898file::file-1741681906725transcript::text',
            source: '1741681888898',
            target: '1741681906725',
            sourceHandle: 'file::file',
            targetHandle: 'transcript::text',
          },
          {
            id: 'xy-edge__1741681888898file::file-1741681905085transcript::text',
            source: '1741681888898',
            target: '1741681905085',
            sourceHandle: 'file::file',
            targetHandle: 'transcript::text',
          },
          {
            id: 'xy-edge__1741681906725text::text-1741681924035summary::text',
            source: '1741681906725',
            target: '1741681924035',
            sourceHandle: 'text::text',
            targetHandle: 'summary::text',
          },
          {
            id: 'xy-edge__1741681905085text::text-1741681924035questionsAndAnswers::text',
            source: '1741681905085',
            target: '1741681924035',
            sourceHandle: 'text::text',
            targetHandle: 'questionsAndAnswers::text',
          },
          {
            id: 'xy-edge__1741681924035text::text-1741681902499text::text',
            source: '1741681924035',
            target: '1741681902499',
            sourceHandle: 'text::text',
            targetHandle: 'text::text',
          },
          {
            id: 'xy-edge__1741681888898filepath::path-1741681902499path::path',
            source: '1741681888898',
            target: '1741681902499',
            sourceHandle: 'filepath::path',
            targetHandle: 'path::path',
          },
        ],
        nodes: [
          {
            id: '1741681888898',
            data: {
              type: 'transcript',
              multiple: false,
            },
            type: 'file',
            position: {
              x: -450,
              y: -405,
            },
          },
          {
            id: '1741681902499',
            data: {
              name: t('Intervjuanteckning'),
            },
            type: 'saveNotes',
            position: {
              x: 840,
              y: -225,
            },
          },
          {
            id: '1741681905085',
            data: {
              prompt: t(
                'Extrahera alla intressanta frågor som är relevanta till ämnet till en sammanfattning av svaret. Skriv frågorna i bold tillsammans med tiden när de ställs, och svaren på en ny rad. Följ mallen (byt ut variablerna i {}): `**FRÅGA** (START_TID_FÖR_FRÅGAN) \\n\\n SVAR` (Skriv inte SVAR: i svaret).\n\n{transcript}'
              ),
            },
            type: 'llm',
            position: {
              x: 0,
              y: 240,
            },
          },
          {
            id: '1741681906725',
            data: {
              prompt: t(
                'Sammanfatta de viktigaste delar av transkriberingen. Sammanfattningen ska vara kortfattad och begränsa svaret till högst 400 ord. Undvik rubriker, underrubriker och listor, utan svara endast med den sammanfattande texten.\n\n{transcript}'
              ),
            },
            type: 'llm',
            position: {
              x: 0,
              y: -120,
            },
          },
          {
            id: '1741681924035',
            data: {
              template: t(
                '# Summering\n{summary}\n\n# Frågor och svar\n{questionsAndAnswers}'
              ),
            },
            type: 'template',
            position: {
              x: 390,
              y: 90,
            },
          },
        ],
      },
    },
    {
      id: 'summary',
      title: t('Summeringsmall'),
      description: t('Sammanfatta ett möte och skriv ut uppföljningspunkter'),
      icons: [EditOutlinedIcon, SummarizeOutlinedIcon],
      popular: true,
      data: {
        edges: [
          {
            id: 'xy-edge__1742391806624file::file-1742391923390text::text',
            source: '1742391806624',
            target: '1742391923390',
            sourceHandle: 'file::file',
            targetHandle: 'text::text',
          },
          {
            id: 'xy-edge__1742391806624file::file-1742391926080transcript::text',
            source: '1742391806624',
            target: '1742391926080',
            sourceHandle: 'file::file',
            targetHandle: 'transcript::text',
          },
          {
            id: 'xy-edge__1742391923390text::text-1742391934478summary::text',
            source: '1742391923390',
            target: '1742391934478',
            sourceHandle: 'text::text',
            targetHandle: 'summary::text',
          },
          {
            id: 'xy-edge__1742391926080text::text-1742391934478actions::text',
            source: '1742391926080',
            target: '1742391934478',
            sourceHandle: 'text::text',
            targetHandle: 'actions::text',
          },
          {
            id: 'xy-edge__1742391934478text::text-1742391930641text::text',
            source: '1742391934478',
            target: '1742391930641',
            sourceHandle: 'text::text',
            targetHandle: 'text::text',
          },
          {
            id: 'xy-edge__1742391806624filepath::path-1742391930641path::path',
            source: '1742391806624',
            target: '1742391930641',
            sourceHandle: 'filepath::path',
            targetHandle: 'path::path',
          },
        ],
        nodes: [
          {
            id: '1742391806624',
            data: {
              type: 'transcript',
              multiple: false,
            },
            type: 'file',
            position: {
              x: -165,
              y: -30,
            },
          },
          {
            id: '1742391923390',
            data: {
              prompt: t(
                'Sammanfatta de viktigaste delar av innehållet. Sammanfattningen ska vara kortfattad och begränsa svaret till högst 400 ord. Undvik rubriker, underrubriker och listor, utan svara endast med den sammanfattande texten.\n\n{text}'
              ),
            },
            type: 'llm',
            position: {
              x: 255,
              y: 270,
            },
          },
          {
            id: '1742391926080',
            data: {
              prompt: t(
                'Extrahera alla uppföljningspunkter som beslutas i transkriberingen. Skriv alla punkter i en lista tillsammans med tiden de beslutas. Följ mallen: `* UPPFÖLJNINGSPUNKTEN (START_TID_FÖR_BESLUTET)\n\n{transcript}'
              ),
            },
            type: 'llm',
            position: {
              x: 255,
              y: 615,
            },
          },
          {
            id: '1742391930641',
            data: {
              name: t('Mötesanteckningar'),
            },
            type: 'saveNotes',
            position: {
              x: 1050,
              y: 15,
            },
          },
          {
            id: '1742391934478',
            data: {
              template: t(
                '# Summering\n{summary}\n\n# Uppföljningspunkter\n{actions}'
              ),
            },
            type: 'template',
            position: {
              x: 660,
              y: 360,
            },
          },
        ],
      },
    },
    {
      id: 'slack-summary',
      title: t('Sammanfattning till Slack'),
      description: t('Sammanfatta ett möte och skicka till en användare eller kanal i Slack'),
      icons: [slackLogo, SummarizeOutlinedIcon],
      requiredIntegrations: ['slack'],
      data: {
        edges: [
          {
            id: 'xy-edge__1745918416268file::file-1745927530374transcription::text',
            source: '1745918416268',
            target: '1745927530374',
            sourceHandle: 'file::file',
            targetHandle: 'transcription::text',
          },
          {
            id: 'xy-edge__1745927530374text::text-1745918449310message::any',
            source: '1745927530374',
            target: '1745918449310',
            sourceHandle: 'text::text',
            targetHandle: 'message::any',
          },
          {
            id: 'xy-edge__1745918416268filename::text-1745934642390filename::text',
            source: '1745918416268',
            target: '1745934642390',
            sourceHandle: 'filename::text',
            targetHandle: 'filename::text',
          },
          {
            id: 'xy-edge__1745934642390text::text-1745918449310header::any',
            source: '1745934642390',
            target: '1745918449310',
            sourceHandle: 'text::text',
            targetHandle: 'header::any',
          },
        ],
        nodes: [
          {
            id: '1745918416268',
            data: {
              type: 'all',
              multiple: false,
            },
            type: 'file',
            position: {
              x: -780,
              y: -465,
            },
          },
          {
            id: '1745918449310',
            data: {
              channel: '',
            },
            type: 'slack',
            position: {
              x: 75,
              y: -525,
            },
          },
          {
            id: '1745927530374',
            data: {
              prompt: t(
                `${BASE_SUMMARY_PROMPT}\n\n{transcription}`
              ),
            },
            type: 'llm',
            position: {
              x: -345,
              y: -360,
            },
          },
          {
            id: '1745934642390',
            data: {
              template: `${t('Sammanfattning av')} "{filename}"`,
            },
            type: 'template',
            position: {
              x: -390,
              y: -720,
            },
          },
        ],
      },
    },
    {
      id: 'email-summary',
      title: t('Sammanfattning till e-post'),
      description: t(
        'Sammanfatta ett möte och skicka sammanfattningen till mötetsdeltagare med e-post'
      ),
      icons: [EmailOutlinedIcon, SummarizeOutlinedIcon],
      data: {
        edges: [
          {
            id: 'xy-edge__1747140892765emails::email-1747140901245emails::email',
            source: '1747140892765',
            target: '1747140901245',
            sourceHandle: 'emails::email',
            targetHandle: 'emails::email',
          },
          {
            id: 'xy-edge__1747140892765file::file-1747140910109transcription::text',
            source: '1747140892765',
            target: '1747140910109',
            sourceHandle: 'file::file',
            targetHandle: 'transcription::text',
          },
          {
            id: 'xy-edge__1747140910109text::text-1747142270303summary::text',
            source: '1747140910109',
            target: '1747142270303',
            sourceHandle: 'text::text',
            targetHandle: 'summary::text',
          },
          {
            id: 'xy-edge__1747142270303text::text-1747140901245input::any',
            source: '1747142270303',
            target: '1747140901245',
            sourceHandle: 'text::text',
            targetHandle: 'input::any',
          },
        ],
        nodes: [
          {
            id: '1747140892765',
            data: {
              type: 'meetings',
              multiple: false,
            },
            type: 'file',
            position: {
              x: -330,
              y: -120,
            },
          },
          {
            id: '1747140901245',
            data: {
              subject: t('Sammanfattning av ditt möte i Klang.ai'),
            },
            type: 'mail',
            position: {
              x: 765,
              y: 45,
            },
          },
          {
            id: '1747140910109',
            data: {
              prompt: t(
                'Sammanfatta de viktigaste delar av mötet.\n\nSammanfattningen ska vara kortfattad och begränsa svaret till högst fem paragrapher.\n\n{transcription}'
              ),
            },
            type: 'llm',
            position: {
              x: 30,
              y: -240,
            },
          },
          {
            id: '1747142270303',
            data: {
              template: t(
                'Sammanfattning från ditt möte i Klang.ai\n\n{summary}'
              ),
            },
            type: 'template',
            position: {
              x: 390,
              y: -195,
            },
          },
        ],
      },
    },
    {
      id: 'pipedrive-note',
      title: t('Sammanfattning till Pipedrive'),
      description: t(
        'Sammanfatta ett möte och skapa en anteckning i Pipedrive för personen du pratar med.'
      ),
      icons: [pipedriveLogo, SummarizeOutlinedIcon],
      requiredIntegrations: ['pipedrive'],
      data: {
        edges: [
          {
            id: 'xy-edge__1745935419602file::file-1745935454255transcription::text',
            source: '1745935419602',
            target: '1745935454255',
            sourceHandle: 'file::file',
            targetHandle: 'transcription::text',
          },
          {
            id: 'xy-edge__1745935454255text::text-1746012381662input::text',
            source: '1745935454255',
            target: '1746012381662',
            sourceHandle: 'text::text',
            targetHandle: 'input::text',
          },
          {
            id: 'xy-edge__1745935419602emails::email-1746012381662emails::email',
            source: '1745935419602',
            target: '1746012381662',
            sourceHandle: 'emails::email',
            targetHandle: 'emails::email',
          },
        ],
        nodes: [
          {
            id: '1745935419602',
            data: {
              type: 'meetings',
              multiple: false,
            },
            type: 'file',
            position: {
              x: -315,
              y: 15,
            },
          },
          {
            id: '1745935454255',
            data: {
              prompt: `${BASE_SUMMARY_PROMPT}\n\n{transcription}`,
            },
            type: 'llm',
            position: {
              x: 0,
              y: -330,
            },
          },
          {
            id: '1746012381662',
            data: {
              createLead: true,
            },
            type: 'pipedriveNote',
            position: {
              x: 255,
              y: 240,
            },
          },
        ],
      },
    },
    {
      id: 'hubspot-note',
      title: t('Sammanfattning till HubSpot'),
      description: t(
        'Sammanfatta ett möte och skapa en anteckning i HubSpot för personen du pratar med.'
      ),
      icons: [hubspotLogo, SummarizeOutlinedIcon],
      requiredIntegrations: ['hubspot'],
      data: {
        edges: [
          {
            id: 'xy-edge__1746540094140emails::email-1746540087160emails::email',
            source: '1746540094140',
            target: '1746540087160',
            sourceHandle: 'emails::email',
            targetHandle: 'emails::email',
          },
          {
            id: 'xy-edge__1746540094140file::file-1746540106538transcription::text',
            source: '1746540094140',
            target: '1746540106538',
            sourceHandle: 'file::file',
            targetHandle: 'transcription::text',
          },
          {
            id: 'xy-edge__1746540106538text::text-1746540087160input::text',
            source: '1746540106538',
            target: '1746540087160',
            sourceHandle: 'text::text',
            targetHandle: 'input::text',
          },
        ],
        nodes: [
          {
            id: '1746540087160',
            data: {
              createContact: true,
            },
            type: 'hubspotNote',
            position: {
              x: 660,
              y: 60,
            },
          },
          {
            id: '1746540094140',
            data: {
              type: 'meetings',
              multiple: false,
            },
            type: 'file',
            position: {
              x: -120,
              y: -105,
            },
          },
          {
            id: '1746540106538',
            data: {
              prompt: t(
                `${BASE_SUMMARY_PROMPT}\n\n{transcription}`
              ),
            },
            type: 'llm',
            position: {
              x: 285,
              y: -300,
            },
          },
        ],
      },
    },
    {
      id: 'google-drive-summary',
      title: t('Sammanfattning till Google Drive'),
      description: t('Sammanfatta ett möte och spara till en mapp i Google Drive'),
      icons: [googleDriveLogo, SummarizeOutlinedIcon],
      requiredIntegrations: ['googleDrive'],
      data: {
        edges: [
          {
            id: 'xy-edge__1745918416268file::file-1745927530374transcription::text',
            source: '1745918416268',
            target: '1745927530374',
            sourceHandle: 'file::file',
            targetHandle: 'transcription::text',
          },
          {
            id: 'xy-edge__1745927530374text::text-1745918449310text::text',
            source: '1745927530374',
            target: '1745918449310',
            sourceHandle: 'text::text',
            targetHandle: 'text::text',
          },
          {
            id: 'xy-edge__1745918416268filename::text-1745934642390filename::text',
            source: '1745918416268',
            target: '1745934642390',
            sourceHandle: 'filename::text',
            targetHandle: 'filename::text',
          },
          {
            id: 'xy-edge__1745934642390text::text-1745918449310name::text',
            source: '1745934642390',
            target: '1745918449310',
            sourceHandle: 'text::text',
            targetHandle: 'name::text',
          },
        ],
        nodes: [
          {
            id: '1745918416268',
            data: {
              type: 'all',
              multiple: false,
            },
            type: 'file',
            position: {
              x: -780,
              y: -465,
            },
          },
          {
            id: '1745918449310',
            data: {
              folder: '',
            },
            type: 'googleDrive',
            position: {
              x: 75,
              y: -525,
            },
          },
          {
            id: '1745927530374',
            data: {
              prompt: t(
                `${BASE_SUMMARY_PROMPT}\n\n{transcription}`
              ),
            },
            type: 'llm',
            position: {
              x: -345,
              y: -360,
            },
          },
          {
            id: '1745934642390',
            data: {
              template: `${t('Sammanfattning av')} "{filename}"`,
            },
            type: 'template',
            position: {
              x: -390,
              y: -720,
            },
          },
        ],
      },
    },
  ];
}
